/**
 * Chip — interactive, dismissible filter / tag marker (theme T5c §1.3).
 *
 * Distinct shape from Badge (radius-md, not radius-full) so users
 * recognise "this is clickable" vs "this is a status".
 */
import { X } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from './cn';

export interface ChipProps {
  children: ReactNode;
  onDismiss?: () => void;
  /** Render as a link instead of a non-interactive label. */
  onClick?: () => void;
  className?: string;
}

export function Chip({ children, onDismiss, onClick, className }: ChipProps) {
  const interactive = Boolean(onClick || onDismiss);
  return (
    <span
      className={cn(
        'inline-flex h-7 items-center gap-2 rounded-md bg-slate-100 pl-3 pr-2 text-sm font-medium text-slate-700 transition-colors duration-fast ease-standard',
        interactive && 'hover:bg-slate-200',
        className,
      )}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      {children}
      {onDismiss ? (
        <button
          aria-label="Remove"
          className="inline-flex h-4 w-4 items-center justify-center rounded-full text-slate-500 hover:bg-slate-300 hover:text-slate-700"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
        >
          <X aria-hidden size={12} strokeWidth={2} />
        </button>
      ) : null}
    </span>
  );
}
